import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

const IngestionStatus: React.FC = () => {
  const [refreshInterval, setRefreshInterval] = useState(30) // seconds

  // Queries with auto-refresh
  const { data: sources, refetch: refetchSources } = trpc.contentSources.listSources.useQuery(
    { limit: 50 },
    { refetchInterval: refreshInterval * 1000 },
  )

  const { data: contentBySource, refetch: refetchContentBySource } = trpc.contentIngestion.getContentBySource.useQuery(
    { limit: 10, itemsPerSource: 3 },
    { refetchInterval: refreshInterval * 1000 },
  )

  const { data: stats, refetch: refetchStats } = trpc.contentSources.getSourceStats.useQuery(undefined, {
    refetchInterval: refreshInterval * 1000,
  })

  const batchIngestAll = trpc.contentIngestion.batchIngestAll.useMutation({
    onSuccess: () => {
      void refetchSources()
      void refetchContentBySource()
      void refetchStats()
    },
  })

  const manualRefresh = () => {
    void refetchSources()
    void refetchContentBySource()
    void refetchStats()
  }

  const formatTimeAgo = (date: string | Date | null) => {
    if (!date) return "Never"

    const now = new Date()
    const past = date instanceof Date ? date : new Date(date)
    const diffMs = now.getTime() - past.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 1) return "Just now"
    if (diffMinutes < 60) return `${diffMinutes}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  const getStatusColor = (lastIngestedAt: string | Date | null) => {
    if (!lastIngestedAt) return "bg-foregroundx text-gray-800"

    const date = lastIngestedAt instanceof Date ? lastIngestedAt : new Date(lastIngestedAt)
    const diffMs = Date.now() - date.getTime()
    const diffMinutes = diffMs / (1000 * 60)

    if (diffMinutes < 60) return "bg-green-100 text-green-800" // Fresh
    if (diffMinutes < 180) return "bg-yellow-100 text-yellow-800" // Aging
    return "bg-red-100 text-destructive" // Stale
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Ingestion Status</h1>
            <p className="">Real-time monitoring of content ingestion jobs</p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-sm ">Auto-refresh:</label>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-sm"
              >
                <option value={10}>10s</option>
                <option value={30}>30s</option>
                <option value={60}>1m</option>
                <option value={0}>Off</option>
              </select>
            </div>

            <button
              onClick={manualRefresh}
              className="px-3 py-1 bg-foregroundx0 text-white text-sm rounded hover:bg-gray-600"
            >
              Refresh
            </button>

            <button
              onClick={() => batchIngestAll.mutate({})}
              disabled={batchIngestAll.isLoading}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {batchIngestAll.isLoading ? "Running..." : "Ingest All"}
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm ">Total Sources</div>
              <div className="text-xl font-semibold">{stats.totalSources}</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-sm ">Active Sources</div>
              <div className="text-xl font-semibold">{stats.activeSources}</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg">
              <div className="text-sm ">Recently Updated</div>
              <div className="text-xl font-semibold">{stats.recentlyIngested}</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-sm ">News Sources</div>
              <div className="text-xl font-semibold">{stats.newsSources}</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-sm ">X Sources</div>
              <div className="text-xl font-semibold">{stats.xSources}</div>
            </div>
          </div>
        )}
      </div>

      {/* Sources Status Grid */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4">Sources Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources?.sources.map((source) => (
            <div
              key={source.id}
              className={`border rounded-lg p-4 ${source.isActive ? "border-green-200" : "border-border"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium truncate">{source.name}</h3>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    source.type === "news" ? "bg-orange-100 text-orange-800" : "bg-purple-100 text-purple-800"
                  }`}
                >
                  {source.type === "news" ? "News" : "X"}
                </span>
              </div>

              <div className="text-sm  mb-2 truncate">
                {source.type === "news" ? (source.config as any)?.url : `@${(source.config as any)?.username}`}
              </div>

              <div className="flex items-center justify-between">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    source.isActive ? "bg-green-100 text-green-800" : "bg-foregroundx text-gray-800"
                  }`}
                >
                  {source.isActive ? "Active" : "Inactive"}
                </span>

                <span
                  // @ts-ignore TODO: Fix Date to string conversion
                  className={`px-2 py-1 text-xs rounded-full ${getStatusColor(source.lastIngestedAt)}`}
                >
                  {/* @ts-ignore TODO: Fix Date to string conversion */}
                  {formatTimeAgo(source.lastIngestedAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Content by Source */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Recent Content</h2>
        <div className="space-y-6">
          {contentBySource?.map((source) => (
            <div key={source.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <h3 className="font-medium">{source.name}</h3>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      source.type === "news" ? "bg-orange-100 text-orange-800" : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {source.type === "news" ? "News" : "X"}
                  </span>
                  <span className="text-sm text-gray-500">{source.itemCount} recent items</span>
                </div>

                <span className="text-sm text-gray-500">
                  {/* @ts-ignore TODO: Fix Date to string conversion */}
                  Last: {formatTimeAgo(source.lastIngestedAt)}
                </span>
              </div>

              <div className="space-y-2">
                {source.items.map((item) => (
                  <div key={item.id} className="text-sm border-l-2 border-border pl-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {item.title && <div className="font-medium mb-1 line-clamp-1">{item.title}</div>}
                        <div className=" line-clamp-2">{item.content.substring(0, 150)}...</div>
                      </div>
                      <div className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                        {/* @ts-ignore TODO: Fix Date to string conversion */}
                        {formatTimeAgo(item.publishedAt)}
                      </div>
                    </div>
                  </div>
                ))}

                {source.items.length === 0 && <div className="text-sm text-gray-500 italic">No recent content</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Batch Ingestion Results */}
      {batchIngestAll.data && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-medium text-green-800 mb-2">Batch Ingestion Complete</h3>
          <div className="text-sm text-green-700">Processed {batchIngestAll.data.processedSources} sources</div>
          <div className="mt-2 space-y-1">
            {Object.entries(batchIngestAll.data.results).map(([sourceId, result]) => {
              const source = sources?.sources.find((s) => s.id === sourceId)
              return (
                <div key={sourceId} className="text-xs text-green-600">
                  {source?.name}:{" "}
                  {result.success
                    ? `✓ ${result.itemsIngested} ingested, ${result.itemsSkipped} skipped`
                    : `✗ Failed: ${result.errors?.[0] || "Unknown error"}`}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default IngestionStatus
